import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Grid, Typography, Box, Paper, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
} from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import RateReviewOutlinedIcon from '@material-ui/icons/RateReviewOutlined';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Snackbar from '../../../components/Snackbar';
import ConfirmDialog from '../../../components/ConfirmDialog';
import AutoAwesomeIcon from '../../../components/icons/AutoAwesomeIcon';

import useCarouselStyles from '../ImageTeleaudit/TeleauditCarousel/styles';

import { getQuestionResponseById } from '../../../requests/api/question-response';
import {
  getWalmartAiResultsByQuestionResponse,
  postWalmartAiProductTeleaudit,
} from '../../../requests/api/walmart-ai';

import useStyles from './styles';

// Las etiquetas salen de teleaudit.price.fields.<key>
const PRODUCT_TABLE_COLUMNS = [
  'id', 'description', 'category', 'subCategory', 'brand', 'subBrand', 'size', 'count',
  'eanUpcSku', 'regularPrice', 'offerPrice', 'websearchRegularPrice', 'websearchOfferPrice',
  'confidenceMatch',
];

// Campo del formulario de producto -> campo detectado por la IA en walmart_ai_results
const PRODUCT_FIELDS = {
  description: 'productDescription',
  category: 'productCategory',
  subCategory: 'productSubCategory',
  brand: 'productBrand',
  subBrand: 'productSubBrand',
  size: 'productSize',
  count: 'productCount',
  eanUpcSku: 'productEanUpcSku',
};

const PRICE_FIELDS = ['regularPrice', 'offerPrice', 'offerStartDate', 'offerEndDate'];

const MONEY_FIELDS = ['regularPrice', 'offerPrice'];

// Los precios se muestran siempre con 2 decimales; vacío si no hay valor
const formatPrice = (value) => (
  value === null || value === undefined || value === '' || Number.isNaN(Number(value))
    ? '' : Number(value).toFixed(2)
);

// id que informa la IA en la raíz del JSON crudo (walmart_ai_results.ai_result).
// MySQL puede entregar la columna JSON como string; sin id se muestra vacío.
const getAiResultId = (aiResult) => {
  let parsed = aiResult;
  if (typeof aiResult === 'string') {
    try {
      parsed = JSON.parse(aiResult);
    } catch (error) {
      return '';
    }
  }
  return parsed && parsed.id !== null && parsed.id !== undefined ? parsed.id : '';
};

const PRODUCTS_PER_PAGE = 10;

const EMPTY_RESULTS = [];

// Valores a mostrar de un producto detectado: si ya se editó en teleaudit
// manda lo editado; si no, el catálogo y, en su defecto, lo detectado por la IA.
// Sin resultado devuelve todos los campos vacíos.
const resolveProductFields = (walmartAiResult) => {
  const result = walmartAiResult || {};
  const editedProduct = result.walmartAiProductTeleaudit;
  const productCatalog = result.walmartAiProductCatalog || {};
  const fields = {};

  Object.entries(PRODUCT_FIELDS).forEach(([field, resultField]) => {
    fields[field] = editedProduct
      ? (editedProduct[field] ?? '')
      : (productCatalog[field] ?? result[resultField] ?? '');
  });
  PRICE_FIELDS.forEach((field) => {
    const value = editedProduct ? (editedProduct[field] ?? '') : (result[field] ?? '');
    fields[field] = MONEY_FIELDS.includes(field) ? formatPrice(value) : value;
    if (field === 'offerPrice' && Number(fields[field]) === 0) fields[field] = '';
  });

  return fields;
};

// Pantalla de precio de teleaudit: usa la misma cabecera de contexto e imagen
// que el resto de teleaudit y agrega, debajo, la info de producto detectado
// por la IA
function PriceTeleaudit() {
  const { t } = useTranslation();
  const styles = useStyles();
  const carouselStyles = useCarouselStyles();
  const { taskResponseId, questionResponseId } = useParams();

  const [questionResponse, setQuestionResponse] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);

  // imagesList: array de imágenes con su set de productos
  const [imagesList, setImagesList] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [loadedGondolaUrl, setLoadedGondolaUrl] = useState(null);

  const [walmartAiResultId, setWalmartAiResultId] = useState(null);
  const [productForm, setProductForm] = useState({
    description: '',
    category: '',
    subCategory: '',
    brand: '',
    subBrand: '',
    size: '',
    count: '',
    eanUpcSku: '',
  });
  const [priceForm, setPriceForm] = useState({
    regularPrice: '',
    offerPrice: '',
    offerStartDate: '',
    offerEndDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [snackbar, setSnackbar] = useState({ message: '', severity: 'success' });
  // Destino ({ imageIndex, productIndex }) pedido mientras se editaba; queda a
  // la espera de que el usuario confirme descartar los cambios
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    const fetchQuestionResponse = async () => {
      try {
        const response = await getQuestionResponseById({ questionResponseId });
        const responseData = response.success && response.data.data;

        if (responseData && responseData.questionResponse) {
          setQuestionResponse(responseData.questionResponse);
        } else {
          setSnackbar({
            message: response.data.errorMessage || t('teleaudit.price.errors.generic'),
            severity: 'error',
          });
        }
      } catch (error) {
        setSnackbar({ message: t('teleaudit.price.errors.generic'), severity: 'error' });
      }
    };

    if (questionResponseId) {
      fetchQuestionResponse();
    }
  }, [questionResponseId, t]);

  // Se muestra la compañía dueña del proyecto de la survey, no la del portal.
  useEffect(() => {
    const projectCompany = questionResponse?.SurveyResponse?.Survey?.Project?.Company;
    if (!projectCompany) return;

    setCompanyName(projectCompany.name);
    setCompanyLogo(projectCompany.logoUrl);
  }, [questionResponse]);

  // Trae resultados de lo reconocido por la IA
  const fetchWalmartAiResults = useCallback(async () => {
    if (!questionResponseId) return;

    setLoading(true);
    setTablePage(0);
    try {
      const response = await getWalmartAiResultsByQuestionResponse(questionResponseId);

      if (response.success) {
        // La API retorna un array con un elemento por cada imagen del
        // questionResponseId, cada una con su propio set de productos
        // detectados.
        setImagesList(response.data.data);
        setCurrentImageIndex(0);
        setCurrentProductIndex(0);
      } else {
        setImagesList([]);
        setSnackbar({
          message: response.data.errorMessage || t('teleaudit.price.errors.generic'),
          severity: 'error',
        });
      }
    } catch (error) {
      setImagesList([]);
      setSnackbar({ message: t('teleaudit.price.errors.generic'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [questionResponseId, t]);

  useEffect(() => {
    fetchWalmartAiResults();
  }, [fetchWalmartAiResults]);

  // Imagen actualmente seleccionada (walmart_ai_request) y sus productos
  // detectados. Todo lo que depende del producto (formularios, imagen de
  // góndola) se deriva de este set, no del array completo.
  const currentImage = imagesList[currentImageIndex] || null;
  const currentImageResults = (currentImage && currentImage.walmartAiResults) || EMPTY_RESULTS;

  // Vuelca en los formularios (producto/precio) el producto actualmente
  // seleccionado (currentImageResults[currentProductIndex]).
  useEffect(() => {
    const walmartAiResult = currentImageResults[currentProductIndex] || null;
    const {
      regularPrice, offerPrice, offerStartDate, offerEndDate, ...productFields
    } = resolveProductFields(walmartAiResult);

    setWalmartAiResultId(walmartAiResult ? walmartAiResult.id : null);
    setProductForm(productFields);
    setPriceForm({
      regularPrice, offerPrice, offerStartDate, offerEndDate,
    });
  }, [currentImageResults, currentProductIndex]);

  const currentWalmartAiResult = currentImageResults[currentProductIndex] || null;
  // Todos los productos de una misma imagen comparten la misma foto de
  // góndola (imageUrl).
  const currentProductGondolaUrl = currentWalmartAiResult
    ? currentWalmartAiResult.imageUrl : null;
  const isGondolaImageLoading = Boolean(currentProductGondolaUrl)
    && currentProductGondolaUrl !== loadedGondolaUrl;
  const currentS3UriAiProductPriceDetectionUrl = currentWalmartAiResult
    ? currentWalmartAiResult.s3UriAiProductPriceDetectionUrl : null;

  // confidenceMatch (walmart_ai_results.confidence_match) llega como decimal
  // 0-100 (ej. 81.80);
  const confidenceMatchValue = currentWalmartAiResult
    ? currentWalmartAiResult.confidenceMatch : null;
  const confidenceMatchLabel = confidenceMatchValue !== null && confidenceMatchValue !== undefined
    ? `${Number(confidenceMatchValue).toFixed(0)}%`
    : null;

  // La tabla sólo lista los productos de la imagen seleccionada
  const productTableRows = currentImageResults.map((walmartAiResult, productIndex) => {
    const confidence = walmartAiResult.confidenceMatch;

    return {
      id: getAiResultId(walmartAiResult.aiResult),
      productIndex,
      ...resolveProductFields(walmartAiResult),
      // Los precios de websearch sólo los informa la IA; no se editan en teleaudit
      websearchRegularPrice: formatPrice(walmartAiResult.websearchRegularPrice),
      websearchOfferPrice: formatPrice(walmartAiResult.websearchOfferPrice),
      confidenceMatch: confidence !== null && confidence !== undefined
        ? `${Number(confidence).toFixed(0)}%` : '',
    };
  });

  const paginatedProductRows = productTableRows.slice(
    tablePage * PRODUCTS_PER_PAGE,
    (tablePage + 1) * PRODUCTS_PER_PAGE,
  );

  // Hay cambios sin guardar si algún campo difiere de lo que se cargó para el
  // producto actual. Los precios se comparan con el mismo formato de 2
  // decimales para que "10" y "10.00" no cuenten como cambio.
  const hasUnsavedChanges = () => {
    if (!isEditing) return false;

    const originalFields = resolveProductFields(currentWalmartAiResult);
    const isFieldChanged = (field, value) => {
      const current = MONEY_FIELDS.includes(field) ? formatPrice(value) : value;
      return String(current ?? '') !== String(originalFields[field] ?? '');
    };

    return Object.entries(productForm).some(([field, value]) => isFieldChanged(field, value))
      || Object.entries(priceForm).some(([field, value]) => isFieldChanged(field, value));
  };

  // Cambia de imagen/producto. En modo edición se sigue editando el nuevo
  // producto, con su propio inicio de teleaudit. Al cambiar de imagen la
  // tabla vuelve a su primera página, donde queda seleccionado el primer producto.
  const goTo = (imageIndex, productIndex) => {
    if (imageIndex !== currentImageIndex) setTablePage(0);
    setCurrentImageIndex(imageIndex);
    setCurrentProductIndex(productIndex);
    if (isEditing) setStartDate(new Date().toISOString());
  };

  // Si hay cambios sin guardar, pide confirmar antes de descartarlos
  const navigateTo = (imageIndex, productIndex) => {
    if (imageIndex === currentImageIndex && productIndex === currentProductIndex) return;

    if (hasUnsavedChanges()) {
      setPendingNavigation({ imageIndex, productIndex });
      return;
    }

    goTo(imageIndex, productIndex);
  };

  const handleConfirmDiscard = () => {
    goTo(pendingNavigation.imageIndex, pendingNavigation.productIndex);
    setPendingNavigation(null);
  };

  const handlePrevImage = () => {
    if (!imagesList.length) return;
    navigateTo((currentImageIndex - 1 + imagesList.length) % imagesList.length, 0);
  };

  const handleNextImage = () => {
    if (!imagesList.length) return;
    navigateTo((currentImageIndex + 1) % imagesList.length, 0);
  };

  const handleProductSelect = (productIndex) => {
    navigateTo(currentImageIndex, productIndex);
  };

  const handleProductChange = (field) => (event) => {
    setProductForm({ ...productForm, [field]: event.target.value });
  };

  const handlePriceChange = (field) => (event) => {
    setPriceForm({ ...priceForm, [field]: event.target.value });
  };

  const handleEdit = () => {
    if (!currentImageResults.length) return;

    setStartDate(new Date().toISOString());
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!isEditing || !walmartAiResultId || !startDate) return;

    setLoading(true);
    try {
      const response = await postWalmartAiProductTeleaudit({
        walmartAiResultId,
        ...productForm,
        regularPrice: priceForm.regularPrice === '' ? null : Number(priceForm.regularPrice),
        offerPrice: priceForm.offerPrice === '' ? null : Number(priceForm.offerPrice),
        offerStartDate: priceForm.offerStartDate || null,
        offerEndDate: priceForm.offerEndDate || null,
        startDate,
      });

      if (response.success) {
        // Las imágenes no cambian al guardar: basta con reemplazar el producto
        // editado del resultado guardado para que tabla y formulario lo reflejen
        const { walmartAiProductTeleaudit } = response.data.data;
        setImagesList((images) => images.map((image) => ({
          ...image,
          walmartAiResults: (image.walmartAiResults || []).map((walmartAiResult) => (
            walmartAiResult.id === walmartAiResultId
              ? { ...walmartAiResult, walmartAiProductTeleaudit }
              : walmartAiResult
          )),
        })));
        setSnackbar({ message: t('teleaudit.price.messages.saved'), severity: 'success' });
        setIsEditing(false);
        setStartDate(null);
      } else {
        setSnackbar({
          message: response.data.errorMessage || t('teleaudit.price.errors.generic'),
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({ message: t('teleaudit.price.errors.generic'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.content}>
      <Grid container style={{ minHeight: '100vh' }} spacing={4}>
        <Grid item xs={12} style={{ minWidth: 0 }}>
          <div className={carouselStyles.header} style={{ alignItems: 'center' }}>
            <Box flexGrow={1}>
              <Typography variant="h1" gutterBottom className={carouselStyles.title}>
                {t('teleaudit.price.title')}
              </Typography>
            </Box>

            {/* Navigation Buttons */}
            <Box display="flex" alignItems="center">
              <Box mr={2}>
                <Button
                  align="right"
                  id="back-btn"
                  text={t('teleaudit.buttons.back')}
                  variant="contained"
                  height="40px"
                  width="170px"
                  backgroundColor="#2C9DC9"
                  color="white"
                  borderRadius="10px"
                  component={Link}
                  to={`/teleaudits/${taskResponseId}/price`}
                />
              </Box>
              <Box>
                <Button
                  id="edit-btn"
                  variant="contained"
                  text={t('teleaudit.price.buttons.edit')}
                  height="40px"
                  width="170px"
                  backgroundColor={loading || isEditing || !currentImageResults.length
                    ? 'theme-custom-disabledButtonBackground' : '#2C9DC9'}
                  color="white"
                  border={loading || isEditing || !currentImageResults.length
                    ? 'none' : undefined}
                  borderRadius="10px"
                  onClick={handleEdit}
                  disabled={loading || isEditing || !currentImageResults.length}
                />
              </Box>
            </Box>
          </div>

          <Grid container direction="column" alignItems="stretch" spacing={0}>
            {/* Company Card */}
            <Grid
              item
              container
              className={carouselStyles.companyCard}
              direction="column"
              alignItems="left"
              style={{ gap: '16px' }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="left"
                sx={{
                  width: '100%',
                  gap: '16px',
                  flexDirection: 'row',
                  '@media (max-width:1210px)': {
                    flexDirection: 'column',
                  },
                }}
              >
                {/* Company */}
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Box mr={2}>
                    {companyLogo ? (
                      <img
                        alt={companyName}
                        src={companyLogo}
                        width="auto"
                        height="70px"
                        style={{ objectFit: 'cover' }}
                      />
                    ) : null}
                  </Box>

                  <Box>
                    <Typography variant="h2">{companyName}</Typography>
                    <Typography variant="h4">
                      {questionResponse
                      && questionResponse.SurveyResponse.TaskInstance.Category
                        ? questionResponse.SurveyResponse.TaskInstance.Category.name
                        : t('teleaudit.noCategory')}
                    </Typography>
                  </Box>
                </Box>

                {/* Response Information */}
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Box mr={1}>
                    <AssignmentIcon color="primary" fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h2">
                      {questionResponse ? questionResponse.Question.title : null}
                    </Typography>
                    <Typography variant="h4">
                      ID: {questionResponseId}
                    </Typography>
                  </Box>
                </Box>

                {/* Survey */}
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Box mr={1}>
                    <RateReviewOutlinedIcon color="primary" fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h4">
                      {questionResponse
                        ? t('teleaudit.price.responseId', { id: questionResponse.surveyResponseId }) : ''}
                    </Typography>
                    <Typography variant="h2">
                      {questionResponse && questionResponse.SurveyResponse.Survey
                        ? questionResponse.SurveyResponse.Survey.title : ''}
                    </Typography>
                    <Typography variant="h4">
                      {questionResponse && questionResponse.SurveyResponse.Survey
                        ? questionResponse.SurveyResponse.Survey.type : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {currentProductGondolaUrl ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              className={styles.productImageBox}
            >
              {isGondolaImageLoading ? (
                <div className={styles.gondolaImageLoader}>
                  <CircularProgress aria-label={t('loading')} />
                </div>
              ) : null}
              <img
                src={currentProductGondolaUrl}
                alt={t('teleaudit.price.gondolaImageAlt', {
                  current: currentImageIndex + 1,
                  total: imagesList.length,
                })}
                className={styles.productGondolaImage}
                style={isGondolaImageLoading ? { visibility: 'hidden' } : undefined}
                onLoad={() => setLoadedGondolaUrl(currentProductGondolaUrl)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/no_preview.png';
                  setLoadedGondolaUrl(currentProductGondolaUrl);
                }}
              />
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              className={styles.productImageBox}
            >
            </Box>
          )}

          {imagesList.length > 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              className={styles.imageNavRow}
            >
              <IconButton
                id="prev-image-btn"
                aria-label={t('teleaudit.buttons.previousImage')}
                onClick={handlePrevImage}
                size="small"
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              <Typography className={styles.imageNavCounter}>
                {currentImageIndex + 1} / {imagesList.length}
              </Typography>
              <IconButton
                id="next-image-btn"
                aria-label={t('teleaudit.buttons.nextImage')}
                onClick={handleNextImage}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}

          {productTableRows.length > 0 ? (
            <Box className={styles.productsTableWrapper}>
              <TableContainer className={styles.productsTableContainer}>
                <Table
                  size="small"
                  stickyHeader
                  aria-label={t('teleaudit.price.table.ariaLabel')}
                  className={styles.productsTable}
                >
                  <TableHead>
                    <TableRow>
                      {PRODUCT_TABLE_COLUMNS.map((column) => (
                        <TableCell
                          key={column}
                          className={styles.productsTableHeader}
                        >
                          {t(`teleaudit.price.fields.${column}`)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProductRows.map((row) => (
                      <TableRow
                        hover
                        selected={row.productIndex === currentProductIndex}
                        key={`${currentImageIndex}-${row.productIndex}-${row.id || 'product'}`}
                        onClick={() => handleProductSelect(row.productIndex)}
                        className={styles.productsTableRow}
                      >
                        {PRODUCT_TABLE_COLUMNS.map((column) => (
                          <TableCell
                            key={column}
                            className={styles.productsTableCell}
                          >
                            {row[column]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={productTableRows.length}
                page={tablePage}
                onPageChange={(_event, newPage) => setTablePage(newPage)}
                rowsPerPage={PRODUCTS_PER_PAGE}
                rowsPerPageOptions={[PRODUCTS_PER_PAGE]}
                labelRowsPerPage={t('teleaudit.price.table.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('teleaudit.price.table.displayedRows', { from, to, count })}
              />
            </Box>
          ) : null}

          {/* Formulario */}
          <Paper className={styles.paper}>
            <Typography
              className={styles.sectionTitle}
              style={{ marginTop: 0 }}
            >
              {t('teleaudit.price.priceSection')}
            </Typography>
            <div className={styles.priceFieldsGrid}>
              <TextField
                id="regular-price"
                label={t('teleaudit.price.fields.regularPrice')}
                type="number"
                value={priceForm.regularPrice.toString()}
                onChange={handlePriceChange('regularPrice')}
                fullWidth
                disabled={!isEditing}
                className={styles.textField}
              />
              <TextField
                id="offer-price"
                label={t('teleaudit.price.fields.offerPrice')}
                type="number"
                value={priceForm.offerPrice.toString()}
                onChange={handlePriceChange('offerPrice')}
                fullWidth
                disabled={!isEditing}
                className={styles.textField}
              />
              <TextField
                id="offer-start-date"
                label={t('teleaudit.price.fields.offerStartDate')}
                type="date"
                value={priceForm.offerStartDate.toString()}
                onChange={handlePriceChange('offerStartDate')}
                fullWidth
                disabled={!isEditing}
                className={styles.textField}
              />
              <TextField
                id="offer-end-date"
                label={t('teleaudit.price.fields.offerEndDate')}
                type="date"
                value={priceForm.offerEndDate.toString()}
                onChange={handlePriceChange('offerEndDate')}
                fullWidth
                disabled={!isEditing}
                className={styles.textField}
              />
            </div>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              className={styles.productNavRow}
            >
              <Box display="flex" alignItems="center">
                <Typography
                  className={styles.sectionTitle}
                  style={{ marginTop: 0, marginBottom: 0 }}
                >
                  {t('teleaudit.price.productSection')}
                </Typography>

                {confidenceMatchLabel ? (
                  <Box
                    id="confidence-match-label"
                    display="flex"
                    alignItems="center"
                    className={styles.confidenceMatch}
                    title={t('teleaudit.price.confidenceMatchTooltip')}
                  >
                    <AutoAwesomeIcon className={styles.confidenceMatchIcon} fontSize="small" />
                    <span>{confidenceMatchLabel}</span>
                  </Box>
                ) : null}
              </Box>

            </Box>

            <div className={styles.productLayout}>
              {currentS3UriAiProductPriceDetectionUrl ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  className={styles.productImageBox}
                >
                  <img
                    src={currentS3UriAiProductPriceDetectionUrl}
                    alt={t('teleaudit.price.productImageAlt')}
                    className={styles.productImage}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/no_preview.png';
                    }}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  className={styles.productImageBox}
                >
                </Box>
              )}

              <div className={styles.productFieldsGrid}>
                <TextField
                  id="product-description"
                  label={t('teleaudit.price.fields.description')}
                  value={productForm.description}
                  onChange={handleProductChange('description')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-category"
                  label={t('teleaudit.price.fields.category')}
                  value={productForm.category}
                  onChange={handleProductChange('category')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-sub-category"
                  label={t('teleaudit.price.fields.subCategory')}
                  value={productForm.subCategory}
                  onChange={handleProductChange('subCategory')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-brand"
                  label={t('teleaudit.price.fields.brand')}
                  value={productForm.brand}
                  onChange={handleProductChange('brand')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-sub-brand"
                  label={t('teleaudit.price.fields.subBrand')}
                  value={productForm.subBrand}
                  onChange={handleProductChange('subBrand')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-size"
                  label={t('teleaudit.price.fields.size')}
                  value={productForm.size}
                  onChange={handleProductChange('size')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-count"
                  label={t('teleaudit.price.fields.count')}
                  value={productForm.count}
                  onChange={handleProductChange('count')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
                <TextField
                  id="product-sku"
                  label={t('teleaudit.price.fields.eanUpcSku')}
                  value={productForm.eanUpcSku}
                  onChange={handleProductChange('eanUpcSku')}
                  fullWidth
                  disabled={!isEditing}
                  className={styles.textField}
                />
              </div>
            </div>

            <div className={styles.buttonRow}>
              <Button
                id="save-btn"
                variant="contained"
                text={t('teleaudit.price.buttons.save')}
                height="40px"
                width="170px"
                backgroundColor={loading || !isEditing || !walmartAiResultId || !startDate
                  ? 'theme-custom-disabledButtonBackground' : '#2C9DC9'}
                color="white"
                border={loading || !isEditing || !walmartAiResultId || !startDate
                  ? 'none' : undefined}
                borderRadius="10px"
                onClick={handleSave}
                disabled={loading || !isEditing || !walmartAiResultId || !startDate}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.message !== ''}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, message: '' }))}
      />
      <ConfirmDialog
        open={pendingNavigation !== null}
        title={t('teleaudit.price.discardDialog.title')}
        message={t('teleaudit.price.discardDialog.message')}
        confirmText={t('teleaudit.price.discardDialog.confirm')}
        cancelText={t('teleaudit.price.discardDialog.cancel')}
        buttonWidth="170px"
        confirmBackgroundColor="theme-custom-dangerButtonBackground"
        confirmColor="white"
        confirmBorder="none"
        cancelBackgroundColor="theme-custom-solidButtonBackground"
        cancelColor="theme-custom-solidButtonText"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setPendingNavigation(null)}
      />
    </div>
  );
}

export default PriceTeleaudit;
